import React, { useEffect, useState, useRef } from "react";

import InformationModal from "../../../InformationModal";
import { Button, ListGroup, ListGroupItem } from "reactstrap";
import NumericFormat from "../../../NumericFormat";
import QuoteItemEditOptionsModal from "./QuoteItemEditOptionsModal";
import { useQuoteItem, ACTIONS } from "../../../../providers/quoteItemProvider";
import { utils } from "../../../../utils/utils";
import { useJobDetails } from "../../../../providers/jobDetailsProvider";

const QuoteItemEquipmentRental = ({
  quoteItemEquipmentRentals,
  setQuoteItemEquipmentRentals,
}) => {
  const [quoteItemContext, setQuoteItemContext] = useQuoteItem();
  const [jobDetails] = useJobDetails();

  const isJobLocked = jobDetails.job.lockDate;

  const [editOptionsModal, setEditOptionsModal] = useState();
  const [totalCost, setTotalCost] = useState(0);
  const contextVersionRef = useRef(0);
  const skipNextUpdateRef = useRef(false);

  useEffect(() => {
    // Skip updates during save/refresh cycle
    if (skipNextUpdateRef.current) {
      return;
    }

    const quoteItemEquipmentRentals =
      quoteItemContext.quoteItem.quoteItemEquipmentRentals?.reduce((p, c) => {
        p[c.id] = c;
        return p;
      }, {}) || {};
    setQuoteItemEquipmentRentals(quoteItemEquipmentRentals);
    contextVersionRef.current++;
  }, [quoteItemContext.quoteItem, setQuoteItemEquipmentRentals]);

  // Reset skip flag when refresh completes
  useEffect(() => {
    if (quoteItemContext.refresh) {
      skipNextUpdateRef.current = false;
    }
  }, [quoteItemContext.refresh]);

  const onFieldChange = (equipmentRentalId, field, value) => {
    // Block context updates while user is making changes
    skipNextUpdateRef.current = true;
    setQuoteItemEquipmentRentals({
      ...quoteItemEquipmentRentals,
      [equipmentRentalId]: {
        ...quoteItemEquipmentRentals[equipmentRentalId],
        [field]: value || 0,
      },
    });
  };

  useEffect(() => {
    const totalCost = Object.values(quoteItemEquipmentRentals).reduce(
      (p, c) => p + parseFloat(c.cost || 0),
      0
    );
    setTotalCost(totalCost);
  }, [quoteItemEquipmentRentals]);

  const [informationModal, setInformationModal] = useState({
    isOpen: false,
    title: "",
    body: "",
    onClose: null,
  });

  return (
    <>
      <ListGroup>
        <ListGroupItem className="border-radius-0 d-flex justify-content-between align-items-center bg-lighter">
          <small className="font-weight-bold text-muted">
            Equipment Rental
          </small>
          {isJobLocked ? null : (
            <div className="d-flex">
              <Button
                className="rounded"
                size="sm"
                onClick={() => setEditOptionsModal(true)}
              >
                Edit Options
              </Button>
            </div>
          )}
        </ListGroupItem>
        {quoteItemContext.quoteItem.quoteItemEquipmentRentals
          ?.sort((x, y) =>
            parseInt(x.equipmentRental.id) < parseInt(y.equipmentRental.id)
              ? -1
              : 1
          )
          .map((equipmentRental) => {
            const cost =
              quoteItemEquipmentRentals[equipmentRental.id]?.cost || "";
            return (
              <ListGroupItem
                key={equipmentRental.id}
                className="border-radius-0 d-flex justify-content-between align-items-center py-0"
              >
                <span>{equipmentRental?.equipmentRental?.name}</span>
                <div className="width-150">
                  <NumericFormat
                    required={true}
                    readOnly={isJobLocked}
                    maxLength={20}
                    allowNegative={false}
                    decimalScale={2}
                    className="form-control border text-right m-1"
                    value={cost}
                    placeholder={"Enter cost..."}
                    thousandSeparator={true}
                    prefix={"$"}
                    onFocus={() => {
                      if (!cost) {
                        onFieldChange(equipmentRental.id, "cost", 0);
                      }
                    }}
                    onValueChange={(e) =>
                      onFieldChange(equipmentRental.id, "cost", e.value)
                    }
                  />
                </div>
              </ListGroupItem>
            );
          })}
        <ListGroupItem className="border-radius-0 d-flex flex-column py-0">
          <div className="d-flex justify-content-between align-items-center">
            <span className="font-weight-bold">Total Cost</span>
            <div className="width-150">
              <div readOnly className="form-control border text-right m-1">
                {utils.formatCurrency(totalCost)}
              </div>
            </div>
          </div>
        </ListGroupItem>
      </ListGroup>
      {informationModal.isOpen ? (
        <InformationModal
          title={informationModal.title}
          body={informationModal.body}
          onClose={() =>
            informationModal.onClose
              ? informationModal.onClose()
              : setInformationModal({ isOpen: false, title: "", body: "" })
          }
        />
      ) : null}
      {editOptionsModal ? (
        <QuoteItemEditOptionsModal
          title="Equipment Rental"
          field="quoteItemEquipmentRentals"
          idField="equipmentRentalId"
          quoteItem={quoteItemContext.quoteItem}
          onSubmit={() => {
            setEditOptionsModal(false);
            setQuoteItemContext({
              action: ACTIONS.REFRESH,
            });
          }}
          onClose={() => setEditOptionsModal(false)}
        />
      ) : null}
    </>
  );
};

export default QuoteItemEquipmentRental;
